import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Bolt, Rocket, Star } from "@mui/icons-material";
import { PlanCard, FAQSection } from "../components/plans";
import { SPACING } from "../theme/constants";
import {
  fetchPlans,
  fetchMyPlan,
  subscribeToPlan,
} from "../redux/slices/planSlice";

// Icons map for plan tiers
const planIcons = {
  Free: <Bolt />,
  Starter: <Bolt />,
  Professional: <Rocket />,
  Enterprise: <Star />,
};

const planColors = {
  Free: "#78909c",
  Starter: "#0073e6",
  Professional: "#00c9a7",
  Enterprise: "#ff9800",
};

const Plans = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { plans, myPlan, usage, loading } = useSelector((state) => state.plan);
  const { user } = useSelector((state) => state.user);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchMyPlan());
  }, [dispatch]);

  // Show message if redirected from canceled checkout
  useEffect(() => {
    if (searchParams.get("canceled") === "true") {
      setSnackbar({
        open: true,
        message: "Checkout was canceled. No charges were made.",
        severity: "info",
      });
    }
  }, [searchParams]);

  const currentPlanName = myPlan?.name || user?.plan?.name || "Free";

  const handleUpgrade = async (plan) => {
    if (plan.name === currentPlanName) return;

    try {
      const result = await dispatch(
        subscribeToPlan({ planId: plan.id, billingInterval }),
      ).unwrap();

      // If the API returns a checkout URL, redirect to Stripe
      if (result.data?.requiresCheckout && result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
        return;
      }

      setSnackbar({
        open: true,
        message: `Successfully switched to ${plan.name} plan!`,
        severity: "success",
      });
      dispatch(fetchMyPlan());
      dispatch(fetchPlans());
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || error?.message || "Failed to update plan",
        severity: "error",
      });
    }
  };

  // Build displayable plans from API data
  const displayPlans =
    plans.length > 0
      ? plans.map((plan) => {
          const monthlyPrice = Number(plan.priceMonthly);
          const yearlyPrice = plan.priceYearly
            ? Number(plan.priceYearly)
            : null;
          const yearlyMonthly = yearlyPrice ? yearlyPrice / 12 : null;
          const displayPrice =
            billingInterval === "yearly" && yearlyMonthly
              ? yearlyMonthly
              : monthlyPrice;
          const savings =
            yearlyMonthly && monthlyPrice > 0
              ? Math.round(
                  ((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) *
                    100,
                )
              : 0;

          return {
            ...plan,
            price: displayPrice,
            yearlyTotal: yearlyPrice,
            savings,
            billingLabel:
              billingInterval === "yearly" && yearlyPrice
                ? `$${yearlyPrice}/year (save ${savings}%)`
                : "Billed monthly",
            icon: planIcons[plan.name] || <Bolt />,
            color: planColors[plan.name] || "#0073e6",
            popular: plan.name === "Professional",
            features: [
              {
                text:
                  plan.maxSites >= 999
                    ? "Unlimited Sites"
                    : `${plan.maxSites} WordPress Sites`,
                included: true,
              },
              { text: `${plan.maxStorageGB} GB Storage`, included: true },
              {
                text: "Automated Backups",
                included: plan.features?.automatedBackups ?? false,
              },
              {
                text: "SSL Certificates",
                included: plan.features?.sslCertificates ?? true,
              },
              {
                text: "24/7 Support",
                included: plan.features?.support === "24/7",
              },
              {
                text: "CDN Integration",
                included: plan.features?.cdnIntegration ?? false,
              },
              {
                text: "Staging Environment",
                included: plan.features?.stagingEnvironment ?? false,
              },
              {
                text: "Priority Support",
                included: plan.features?.prioritySupport ?? false,
              },
              ...(plan.trialDays > 0
                ? [{ text: `${plan.trialDays}-day free trial`, included: true }]
                : []),
            ],
          };
        })
      : [];

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      {/* Header row: title/toggle left, usage right */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: usage ? "1fr 1fr" : "1fr" },
          gap: 3,
          alignItems: "center",
          mb: SPACING.xl,
          width: "100%",
        }}
      >
        {/* Left: title + billing toggle + current plan chip */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            height: "100%",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Choose Your Plan
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: SPACING.md }}
          >
            Select the perfect plan for your WordPress hosting needs
          </Typography>

          {/* Billing Interval Toggle */}
          <Box sx={{ mb: SPACING.md }}>
            <ToggleButtonGroup
              value={billingInterval}
              exclusive
              onChange={(_, val) => val && setBillingInterval(val)}
              size="small"
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="yearly">
                Yearly
                <Chip
                  label="Save 17%"
                  size="small"
                  color="success"
                  sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Chip
            label={`Current Plan: ${currentPlanName}`}
            color={currentPlanName === "Free" ? "default" : "success"}
            sx={{ fontSize: "0.875rem" }}
          />
        </Box>

        {/* Right: usage stats */}
        {usage && (
          <Box
            sx={{
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2.5,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Your Usage
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  Sites: {usage.sitesUsed} / {usage.sitesLimit}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {usage.sitesPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(usage.sitesPercentage, 100)}
                color={
                  usage.sitesPercentage >= 90
                    ? "error"
                    : usage.sitesPercentage >= 70
                      ? "warning"
                      : "primary"
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  Storage: {usage.storageUsedGB} GB / {usage.storageLimitGB} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {usage.storagePercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(usage.storagePercentage, 100)}
                color={
                  usage.storagePercentage >= 90
                    ? "error"
                    : usage.storagePercentage >= 70
                      ? "warning"
                      : "primary"
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Plans Grid — horizontal scroll on overflow */}
      {loading && displayPlans.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            mb: SPACING.xl,
            overflowX: "auto",
            pb: 2,
            px: 1,
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 3,
              bgcolor: "divider",
            },
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                minWidth: 280,
                flex: "0 0 auto",
                scrollSnapAlign: "start",
              }}
            >
              <Skeleton variant="rounded" height={400} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            mb: SPACING.xl,
            overflowX: "auto",
            pb: 2,
            px: 1,
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 3,
              bgcolor: "divider",
            },
          }}
        >
          {displayPlans.map((plan) => (
            <Box
              key={plan.name}
              sx={{
                minWidth: 280,
                maxWidth: 320,
                flex: "1 0 auto",
                scrollSnapAlign: "start",
              }}
            >
              <PlanCard
                plan={plan}
                currentPlan={currentPlanName}
                onUpgrade={() => handleUpgrade(plan)}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* FAQ Section */}
      <FAQSection />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;
